import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { CssBaseline } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import cssVars from "css-vars-ponyfill";
import { v4 as uuidv4 } from "uuid";
import { Helmet } from "react-helmet";

import Routes from "./Routes";
import { AppContext } from "./libs/contextLib";
import { messenger } from "./libs/messengerLib";
import { whitelabel } from "./libs/whitelabelLib";
import { analytics } from "./libs/analyticsLib";
import { useLocalStorage } from "./libs/storageLib";
import ErrorBoundary from "./components/ErrorBoundary";

import "./App.scss";

function App({ config, ...props }) {
	const history = useHistory();
	const [previousHistoryLocation, setPreviousHistoryLocation] = useState(history.location);
	const [utilityConfig] = useState(config);
	const [gaClientId, setGaClientId] = useLocalStorage("gaClientId", null);
	const [userUuid] = useLocalStorage("uuid", null);
	const [userToken] = useLocalStorage("token", null);

	const onMessageReceivedFromIframe = React.useCallback(
		(e) => {
			console.log("received message", e);
			if (!e || !e.type) return;

			if (e.type === "ga-clientId") {
				setGaClientId(e.data);
				return;
			}
		},
		[setGaClientId]
	);

	useEffect(() => {
		const init = async () => {
			analytics.registerProperties({
				pilotId: utilityConfig.pilotId,
			});

			const cssVariables = whitelabel.cssVariables(utilityConfig);
			cssVars({
				onlyLegacy: false,
				watch: true,
				variables: cssVariables,
				onSuccess: function (cssText, elm, url) {
					let newCssText = cssText;
					Object.keys(cssVariables).forEach(function (k) {
						if (k.indexOf("--custom-replace") !== 0) return; // only replace custom var that css-vars-ponyfill cannot do automatically
						newCssText = newCssText.replace(new RegExp("var[(]" + k + "[)]", "g"), cssVariables[k]);
					});

					if (newCssText !== cssText) {
						cssText = ".css-vars-ponyfill {display:var(--css-vars-ponyfill-hack);}" + newCssText;
					}
					return cssText;
				},
				onComplete: function (cssText, styleNodes, cssVariables, benchmark) {},
				onWarning: function (message) {
					console.log(message); // 1
				},
				onError: function (message, elm, xhr, url) {
					console.log(message); // 1
					console.log(elm); // 2
					console.log(xhr.status); // 3
					console.log(xhr.statusText); // 4
					console.log(url); // 5
				},
			});
		};
		init();
	}, [utilityConfig]);

	useEffect(() => {
		if (!userUuid || !userToken) {
			analytics.clearIdentity();
			return;
		}

		analytics.setIdentity({
			uuid: userUuid,
		});
		analytics.registerProperties({
			uuid: userUuid,
		});
	}, [userUuid, userToken]);

	useEffect(() => {
		if (window.parent.length === 0 && !gaClientId) {
			const clientId = uuidv4();
			setGaClientId(clientId);
			return;
		}
	}, [gaClientId, setGaClientId]);

	useEffect(() => {
		if (window.parent.length === 0) {
			return;
		}

		messenger.init(utilityConfig, onMessageReceivedFromIframe);

		const onHeightChange = (callback) => {
			let currentHeight = document.getElementsByTagName("html")[0].offsetHeight;
			let lastHeight = currentHeight;

			(function run() {
				currentHeight = document.getElementsByTagName("html")[0].offsetHeight;
				if (currentHeight !== lastHeight) callback(currentHeight + 10);
				lastHeight = currentHeight;

				if (document.onHeightChangeTimer) clearTimeout(document.onHeightChangeTimer);
				document.onHeightChangeTimer = setTimeout(run, 250);
			})();
		};

		onHeightChange((height) => {
			messenger.postMessage({
				type: "redraw",
				height: height,
			});
		});

		return () => {
			clearTimeout(document.onHeightChangeTimer);
			messenger.clear(onMessageReceivedFromIframe);
		};
	}, [utilityConfig, onMessageReceivedFromIframe]);

	useEffect(() => {
		history.listen((location) => {
			const isSamePath = location.pathname === previousHistoryLocation.pathname;
			setPreviousHistoryLocation(location);

			if (isSamePath) return;

			window.scroll({ top: 0, behavior: "smooth" });

			messenger.postMessage({
				type: "historyChanged",
			});
		});
	}, [history]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<ThemeProvider theme={whitelabel.theme(utilityConfig)}>
			<AppContext.Provider
				value={{
					utilityConfig,
				}}
			>
				<Helmet
					titleTemplate={"%s - " + whitelabel.stringResources(utilityConfig).siteTitle}
					defaultTitle={whitelabel.stringResources(utilityConfig).siteTitle}
				></Helmet>
				<div className="App">
					<CssBaseline />

					<div className={"container" + (userUuid ? " authenticated" : "")}>
						<ErrorBoundary>
							<Routes />
						</ErrorBoundary>
					</div>
				</div>
			</AppContext.Provider>
		</ThemeProvider>
	);
}

App.propTypes = {
	config: PropTypes.object,
};

export default App;
